import React, { useMemo, useRef, useState, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import {
  MoreVertical,
  Download,
  Trash2,
  Loader2,
  FileText,
} from "lucide-react";

pdfjs.GlobalWorkerOptions.workerSrc =
  "//cdnjs.cloudflare.com/ajax/libs/pdf.js/" +
  pdfjs.version +
  "/pdf.worker.min.js";

/** Human-friendly "x ago" from a Date. */
const timeAgo = (date) => {
  if (!date || Number.isNaN(date.getTime?.())) return "";
  const sec = Math.floor((Date.now() - date.getTime()) / 1000);
  if (sec < 45) return "just now";
  const units = [
    ["year", 31536000],
    ["month", 2592000],
    ["day", 86400],
    ["hour", 3600],
    ["minute", 60],
  ];
  for (const [name, secs] of units) {
    const v = Math.floor(sec / secs);
    if (v >= 1) return `${v} ${name}${v > 1 ? "s" : ""} ago`;
  }
  return "just now";
};

/** Map a job status → a small overlay pill. */
// Map internal job statuses to human-friendly card labels.
const statusMeta = (status) => {
  const s = (status || "").toLowerCase();
  if (["filling", "extracting_fields", "extracting", "processing", "uploading"].includes(s)) {
    return { label: "Working…", spin: true, cls: "bg-[#E4E4E7] text-[#3F3F46]" };
  }
  if (s === "completed") return { label: "Completed", cls: "bg-[#DCFCE7] text-[#15803D]" };
  if (s === "ready_for_review") return { label: "In progress", cls: "bg-[#FEF3C7] text-[#B45309]" };
  if (s === "created") return { label: "Draft", cls: "bg-[#E0E7FF] text-[#4338CA]" };
  if (["failed", "error"].includes(s)) return { label: "Failed", cls: "bg-[#FEE2E2] text-[#B91C1C]" };
  return { label: "Draft", cls: "bg-[#F4F4F5] text-[#52525B]" };
};

const ThumbLoading = () => (
  <div className="flex items-center justify-center w-full h-full text-gray-400">
    <Loader2 className="w-5 h-5 animate-spin" />
  </div>
);

const ThumbFallback = () => (
  <div className="flex flex-col items-center justify-center w-full h-full text-gray-400 gap-1">
    <FileText className="w-8 h-8" />
    <span className="text-[10px]">No preview</span>
  </div>
);

/**
 * One form rendered as a thumbnail card (Adobe-style): first-page PDF preview
 * on a dark card, a red PDF badge, the file name, a "PDF · x ago" meta line,
 * an overlaid status pill, and a hover download/delete menu.
 */
const FormCard = ({ item, onOpen, onDownload, onDelete, deleting }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const fileObj = useMemo(
    () => (item.url ? { url: item.url, withCredentials: false } : null),
    [item.url]
  );

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [menuOpen]);

  const st = statusMeta(item.status);

  return (
    <div
      onClick={() => onOpen(item)}
      className="group relative rounded-xl overflow-hidden bg-[#2B2B2E] cursor-pointer ring-1 ring-black/5 hover:ring-2 hover:ring-[#C95EBE] transition-all"
    >
      {/* Thumbnail */}
      <div className="relative h-[180px] bg-[#3A3A3D] flex items-center justify-center overflow-hidden">
        {fileObj ? (
          <Document
            file={fileObj}
            loading={<ThumbLoading />}
            error={<ThumbFallback />}
            noData={<ThumbFallback />}
          >
            <div className="shadow-lg">
              <Page
                pageNumber={1}
                width={140}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                loading={<ThumbLoading />}
                error={<ThumbFallback />}
              />
            </div>
          </Document>
        ) : (
          <ThumbFallback />
        )}

        {/* Red PDF badge */}
        <div className="absolute bottom-2 left-2 flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#E5472D] text-white text-[9px] font-bold tracking-wide shadow">
          <FileText size={10} />
          PDF
        </div>

        {/* Status pill */}
        <div
          className={`absolute top-2 left-2 flex items-center gap-1 py-1 px-2 rounded-full text-[11px] font-semibold capitalize shadow-sm ${st.cls}`}
        >
          {st.spin && <Loader2 className="w-3 h-3 animate-spin" />}
          {st.label}
        </div>

        {/* Hover menu */}
        <div className="absolute top-2 right-2" ref={menuRef}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((v) => !v);
            }}
            className="p-1.5 rounded-md bg-black/40 text-white opacity-0 group-hover:opacity-100 hover:bg-black/60 transition-opacity"
            title="More actions"
          >
            {deleting ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <MoreVertical size={16} />
            )}
          </button>
          {menuOpen && (
            <div className="absolute right-0 mt-1 w-36 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-20">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpen(false);
                  onDownload(e, item.url, item.name);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-[#3F3F46] hover:bg-gray-50"
              >
                <Download size={14} />
                Download
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpen(false);
                  onDelete(item.jobId, e);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-[#B91C1C] hover:bg-red-50"
              >
                <Trash2 size={14} />
                Delete
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="px-3 py-2.5 bg-[#222225]">
        <div className="text-white text-[13px] font-medium truncate" title={item.name}>
          {item.name}
        </div>
        <div className="text-[#9CA3AF] text-[11px] mt-0.5 flex items-center gap-1.5">
          <span>PDF · {timeAgo(item.rawDate)}</span>
          {item.mode && (
            <span className="px-1.5 py-px rounded bg-white/10 text-[#D4D4D8] uppercase text-[9px] tracking-wide">
              {item.mode === "ai" ? "AI" : "Manual"}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormCard;
